import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation, useParams } from 'react-router-dom';

import { USE_API, changePasswordApi, releasePersonaApi } from '../api/client';
import {
  clearSession,
  clearSessionPersona,
  getCurrentSessionPersona,
  getSession,
  isAdminSession,
  updateSession
} from '../api/session';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Modal } from './Modal';
import { PersonaPicker } from './PersonaPicker';
import { ThemeToggle } from './ThemeToggle';

function PasswordPolicy({ password }: { password: string }) {
  const { t } = useTranslation();
  const checks = [
    { ok: password.length >= 8, label: t('password.minLength') },
    { ok: /[A-Z]/.test(password), label: t('password.upper') },
    { ok: /[a-z]/.test(password), label: t('password.lower') },
    { ok: /[0-9]/.test(password), label: t('password.number') },
    { ok: /[^A-Za-z0-9]/.test(password), label: t('password.special') }
  ];

  return (
    <ul className="password-policy">
      {checks.map((rule) => (
        <li key={rule.label} className={rule.ok ? 'is-ok' : 'is-off'}>
          {rule.ok ? '✓' : '○'} {rule.label}
        </li>
      ))}
    </ul>
  );
}

export function AppShell() {
  const { t } = useTranslation();
  const { caseId } = useParams();
  const location = useLocation();
  const session = getSession();
  const currentPersona = getCurrentSessionPersona();
  const [menuOpen, setMenuOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [personaOpen, setPersonaOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordBusy, setPasswordBusy] = useState(false);
  const isClient = session?.userType === 'CLIENT';
  // Clients cannot change their own password; only Oracle users are ever forced to.
  const mustForcePasswordChange = Boolean(USE_API && !isClient && session?.mustChangePassword);
  const canAccessAdmin = isAdminSession(session);

  // Usage disclaimer shown once per browser session to signed-in clients.
  const DISCLAIMER_KEY = 'finops.disclaimerAck';
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  useEffect(() => {
    if (USE_API && isClient && sessionStorage.getItem(DISCLAIMER_KEY) !== '1') {
      setDisclaimerOpen(true);
    }
  }, [isClient]);
  function acceptDisclaimer() {
    sessionStorage.setItem(DISCLAIMER_KEY, '1');
    setDisclaimerOpen(false);
  }

  useEffect(() => {
    if (mustForcePasswordChange) {
      setPasswordOpen(true);
      setMenuOpen(false);
    }
  }, [mustForcePasswordChange]);

  const effectiveCaseId = useMemo(() => caseId ?? currentPersona?.caseId ?? null, [caseId, currentPersona?.caseId]);
  const canSwitchPersona = Boolean(USE_API && isClient && effectiveCaseId);
  const displayName = session?.displayName ?? session?.userId ?? '—';

  async function logout() {
    if (session?.userType === 'CLIENT' && currentPersona?.caseId && currentPersona?.personaId) {
      await releasePersonaApi(currentPersona.caseId, currentPersona.personaId).catch(() => undefined);
    }
    clearSession();
    window.location.assign('/');
  }

  async function openPersonaSwitch() {
    if (!effectiveCaseId) return;
    setMenuOpen(false);
    if (currentPersona?.caseId === effectiveCaseId && currentPersona.personaId) {
      await releasePersonaApi(effectiveCaseId, currentPersona.personaId).catch(() => undefined);
      clearSessionPersona(effectiveCaseId);
    }
    setPersonaOpen(true);
  }

  async function submitPasswordChange() {
    setPasswordError(null);
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError(t('account.passwordRequired'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError(t('account.passwordMismatch'));
      return;
    }
    if (
      !(
        newPassword.length >= 8 &&
        /[A-Z]/.test(newPassword) &&
        /[a-z]/.test(newPassword) &&
        /[0-9]/.test(newPassword) &&
        /[^A-Za-z0-9]/.test(newPassword)
      )
    ) {
      setPasswordError(t('admin.passwordInvalid'));
      return;
    }

    setPasswordBusy(true);
    try {
      await changePasswordApi({ currentPassword, newPassword });
      updateSession({ mustChangePassword: false });
      setPasswordOpen(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch {
      setPasswordError(t('login.failed'));
    } finally {
      setPasswordBusy(false);
    }
  }

  return (
    <div className="shell">
      <a href="#main-content" className="skip-link">
        {t('a11y.skipToContent')}
      </a>
      <div className="shell__body">
      <aside className="shell__sidebar" aria-label={t('app.title')}>
        <div className="shell__brand">
          <img src="/oracle-logo.png" alt="Oracle" className="shell__logo" />
          <div>
            <div className="shell__brand-title">{t('app.title')}</div>
            <div className="shell__brand-sub">{t('app.subtitle')}</div>
          </div>
        </div>

        <nav className="shell__nav">
          <NavLink to="/" end className="shell__link">
            {t('nav.home')}
          </NavLink>

          <div className="shell__group">
            <span className="shell__group-label">{t('nav.case')}</span>
            {(['config', 'dashboard', 'assessment', 'scoring', 'report', 'connected'] as const).map(
              (seg) =>
                caseId ? (
                  <NavLink
                    key={seg}
                    to={`/case/${caseId}/${seg}`}
                    className="shell__link shell__link--child"
                  >
                    {t(`nav.${seg}`)}
                  </NavLink>
                ) : (
                  <span
                    key={seg}
                    className="shell__link shell__link--child is-disabled"
                    aria-disabled="true"
                  >
                    {t(`nav.${seg}`)}
                  </span>
                )
            )}
          </div>

          {canAccessAdmin && (
            <NavLink to="/admin" className="shell__link">
              {t('nav.admin')}
            </NavLink>
          )}
        </nav>

        {USE_API && session && (
          <div className="shell__usercard">
            <button
              type="button"
              className="shell__userbtn"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
            >
              <span className="shell__userlabel">{t('account.signedInAs')}</span>
              <span className="shell__username">{displayName}</span>
            </button>

            {menuOpen && (
              <div className="shell__usermenu">
                {canSwitchPersona && (
                  <button type="button" className="shell__menuitem" onClick={() => void openPersonaSwitch()}>
                    {t('persona.change')}
                  </button>
                )}
                {!isClient && (
                  <button
                    type="button"
                    className="shell__menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      setPasswordOpen(true);
                    }}
                  >
                    {t('account.changePassword')}
                  </button>
                )}
                <button type="button" className="shell__menuitem" onClick={() => void logout()}>
                  {t('persona.logout')}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="shell__version">
          {t('common.version')} v{__APP_VERSION__}
        </div>
      </aside>

      <div className="shell__main">
        <header className="shell__topbar">
          <div className="shell__topbar-spacer" />
          <div className="shell__topbar-actions">
            <LanguageSwitcher />
            <ThemeToggle compact />
          </div>
        </header>

        <main id="main-content" className="shell__frame" tabIndex={-1} data-route={location.pathname}>
          <Outlet />
        </main>
      </div>
      </div>

      <footer className="shell__footer">
        {t('common.copyright', { year: new Date().getFullYear() })}
      </footer>

      {passwordOpen && (
        <Modal
          title={t('account.changePassword')}
          onClose={() => {
            if (mustForcePasswordChange) return;
            setPasswordOpen(false);
            setPasswordError(null);
          }}
          footer={
            <>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => void submitPasswordChange()}
                disabled={passwordBusy}
              >
                {t('common.save')}
              </button>
              {!mustForcePasswordChange && (
                <button type="button" className="btn btn--ghost" onClick={() => setPasswordOpen(false)}>
                  {t('common.cancel')}
                </button>
              )}
            </>
          }
        >
          {mustForcePasswordChange && <p className="form-note">{t('account.forcePasswordChange')}</p>}
          <label className="field">
            <span className="field__label">{t('account.currentPassword')}</span>
            <input
              className="input"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </label>
          <label className="field">
            <span className="field__label">{t('account.newPassword')}</span>
            <input
              className="input"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </label>
          <PasswordPolicy password={newPassword} />
          <label className="field">
            <span className="field__label">{t('account.confirmPassword')}</span>
            <input
              className="input"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </label>
          {passwordError && <p className="form-error">{passwordError}</p>}
        </Modal>
      )}

      {personaOpen && effectiveCaseId && (
        <Modal title={t('persona.change')} onClose={() => setPersonaOpen(false)}>
          <PersonaPicker
            caseId={effectiveCaseId}
            onClaimed={() => {
              setPersonaOpen(false);
              window.location.assign(`/case/${effectiveCaseId}/assessment`);
            }}
          />
        </Modal>
      )}

      {disclaimerOpen && (
        <Modal
          title={t('disclaimer.title')}
          onClose={acceptDisclaimer}
          footer={
            <button type="button" className="btn btn--primary" onClick={acceptDisclaimer}>
              {t('disclaimer.accept')}
            </button>
          }
        >
          <p>{t('disclaimer.intro')}</p>
          <ul className="disclaimer">
            <li>{t('disclaimer.monitored')}</li>
            <li>{t('disclaimer.restricted')}</li>
            <li>{t('disclaimer.noSharing')}</li>
            <li>{t('disclaimer.noReproduction')}</li>
            <li>{t('disclaimer.credentials')}</li>
          </ul>
        </Modal>
      )}
    </div>
  );
}
